import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as BackIcon } from 'assets/images/back-icon.svg';
import { ReactComponent as CoinIcon } from 'assets/images/coin-icon.svg';
import RecognitionForm from './RecognitionForm';
import CustomLoader from 'components/CustomOverlay';
import { Subtitle2Stronger, Body1Stronger } from '@fluentui/react-components';
import './style.scss';
import clsx from 'clsx';
import { useQuery } from 'react-query';
import { GET_WORKSPACE_CONFIG } from 'Services/apiKeys';
import { getWorkspaceConfig } from 'Services/apiFunctions';
import AddOnDisabled from './addOnDisabled';
import { useRecognitionData } from './useRecognitionData';
import { addDays, format, isAfter, parseISO } from 'date-fns';

const Index = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isTab = location.state?.token;
    const token = searchParams.get('token') || location.state?.token;
    const messageId = searchParams.get('messageId');
    const [creditBalance, setCreditBalance] = useState(0);
    const [submitLoader, setSubmitLoader] = useState(false);
    const [selectedGif, setSelectedGif] = useState('');
    const [addOnDisabledOrExpired, setAddOnDisabledOrExpired] = useState(null);

    const recognitionData = useRecognitionData(token, messageId, setCreditBalance);

    const { data: workspaceConfig, isFetching: workspaceConfigLoading } = useQuery(
        [GET_WORKSPACE_CONFIG, token, messageId],
        getWorkspaceConfig
    );

    const getAddOnExpiryDate = (createdAt, expiryDays) => {
        if (!createdAt || typeof expiryDays !== 'number') return null;
        if (expiryDays === 0) return null;
        const createdDate = parseISO(createdAt);
        const expiryDate = addDays(createdDate, expiryDays);
        if (isAfter(new Date(), expiryDate)) {
            return format(expiryDate, 'dd/MM/yyyy');
        }
        return null;
    };
    const addOnExpired = getAddOnExpiryDate(
        recognitionData?.launchDate ?? recognitionData?.createdAt,
        workspaceConfig?.addOnConfiguration?.allowedAddOnDuration
    );

    const isAddOnDisabled = !workspaceConfig?.addOnConfiguration?.isEnabled;

    useEffect(() => {
        if (isAddOnDisabled || addOnExpired !== null) {
            setAddOnDisabledOrExpired(true);
        } else {
            setAddOnDisabledOrExpired(false);
        }
    }, [workspaceConfig, addOnExpired]);

    const isInitialLoading = recognitionData.recognitionDataLoading || workspaceConfigLoading;
    let addOnLoading = isInitialLoading || submitLoader;

    return (
        <CustomLoader active={addOnLoading}>
            <div className={clsx({ 'recognize-modal': true })}>
                {isTab && (
                    <div className='back-navigation'>
                        <span onClick={() => navigate(-1)}>
                            <BackIcon /> Back
                        </span>
                    </div>
                )}
                {!isInitialLoading && (
                    <>
                        <div className='recognize-modal-heading'>
                            {addOnDisabledOrExpired ? (
                                <Subtitle2Stronger>
                                    {isAddOnDisabled ? `Add On's disabled` : 'Add-On window expired'}
                                </Subtitle2Stronger>
                            ) : (
                                <>
                                    <Subtitle2Stronger>
                                        {messageId ? 'Add-on recognition' : 'Recognize Someone'}
                                    </Subtitle2Stronger>
                                    <div className='recognize-modal-balance'>
                                        <Body1Stronger style={{ opacity: '0.7' }}>Current Balance</Body1Stronger>
                                        <div className='subtitle-4'>
                                            <CoinIcon /> {creditBalance}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {addOnDisabledOrExpired ? (
                            <AddOnDisabled isAddOnDisabled={isAddOnDisabled} addOnExpired={addOnExpired} />
                        ) : (
                            <RecognitionForm
                                authToken={token}
                                setCreditBalance={setCreditBalance}
                                creditBalance={creditBalance}
                                setSubmitLoader={setSubmitLoader}
                                messageId={messageId}
                                selectedGif={selectedGif}
                                setSelectedGif={setSelectedGif}
                                isAddOn={!!messageId}
                                recognitionData={recognitionData}
                            />
                        )}
                    </>
                )}
            </div>
        </CustomLoader>
    );
};

export default Index;
